import { useEffect, useMemo, useRef } from 'react';
import type { RefObject } from 'react';
import { useMeetingStore } from '../../stores/meetingStore';
import { logWarning, logError } from '../../services/logging/logger';

/**
 * Displays the VR room camera view streamed from the headset via LiveKit.
 */
export function RoomCameraView() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const { roomCameraTrack } = useMeetingStore();

  const stream = useMemo(() => buildRoomCameraStream(roomCameraTrack), [roomCameraTrack]);

  useAttachMediaStream(videoRef, stream);

  if (!roomCameraTrack) {
    return null;
  }

  return (
    <div className="relative rounded-lg overflow-hidden bg-gray-900 aspect-[4/3]">
      <div className="absolute top-3 left-3 z-10">
        <span className="px-2 py-1 text-xs font-medium bg-purple-600/90 text-white rounded">
          VR Room View
        </span>
      </div>
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className="w-full h-full object-cover"
      />
    </div>
  );
}

/**
 * Builds a MediaStream for the room camera track.
 */
function buildRoomCameraStream(track: MediaStreamTrack | null): MediaStream | null {
  if (!track) {
    return null;
  }

  return new MediaStream([track]);
}

/**
 * Attaches a MediaStream to a media element ref.
 */
function useAttachMediaStream(
  ref: RefObject<HTMLVideoElement>,
  stream: MediaStream | null
): void {
  useEffect(() => {
    const video = ref.current;
    if (!video) {
      return;
    }

    if (!stream) {
      video.srcObject = null;
      return;
    }

    video.srcObject = stream;
    video.play().catch((error) => {
      logWarning('Room camera playback failed', error);
    });

    const handleError = () => {
      logError('Room camera video element error', video.error?.message ?? 'unknown');
    };

    video.addEventListener('error', handleError);
    return () => {
      video.removeEventListener('error', handleError);
    };
  }, [ref, stream]);
}
